import React from 'react';
import { GameEvent, GameState } from '../../types/game';
import { GameAction } from '../gameReducer';
import { eventStore } from '../../services/EventStore';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';

export class TurnPersistenceError extends Error {
  stage: 'TURN_END' | 'SCORE';

  constructor(stage: 'TURN_END' | 'SCORE', message: string) {
    super(`[${stage}] ${message}`);
    this.name = 'TurnPersistenceError';
    this.stage = stage;
  }
}

export const formatPersistenceErrorMessage = (raw: string): string => {
  const tunnel = raw.match(/TUNNEL_EXIT_TOO_LOW expected>=([0-9]+)\s+got=([0-9]+)/i);
  if (tunnel) {
    const expected = tunnel[1];
    const got = tunnel[2];
    const zone = raw.match(/zone=([0-9-]+)/i)?.[1] || '';
    return `Tunel ${zone || ''}: potrzeba min. ${expected} pkt, próba ${got} odrzucona. Ruch przejęty przez kolejnego gracza.`;
  }
  if (raw.includes('FIRST_ROLL_PENALTY_NOT_ALLOWED_AFTER_900')) {
    return 'Po 900 punktach kara za furę w pierwszym rzucie nie jest naliczana.';
  }
  if (raw.includes('ACTIVE_PLAYER_MISMATCH')) {
    return 'Tura odrzucona: inny gracz jest aktywny na serwerze. Odśwież widok albo zaczekaj na synchronizację.';
  }
  if (raw.includes('ROLL_INDEX_OUT_OF_SYNC')) {
    const expected = raw.match(/expected\s+([0-9]+)/i)?.[1];
    const got = raw.match(/got\s+([0-9]+)/i)?.[1];
    return `Rzut nie został zapisany (licznik: oczekiwano ${expected ?? '?'} / otrzymano ${got ?? '?'}). Odświeżyliśmy widok – wykonaj rzut ponownie.`;
  }
  if (raw.includes('SCORE_MISMATCH')) {
    return 'Punkty się nie zgodziły. Odświeżyliśmy widok – zapisz turę ponownie.';
  }
  if (raw.includes('OVERTAKE_BONUS_MISMATCH')) {
    return 'Wyprzedzenie nie zostało zapisane. Przelicz bonus i wyślij ponownie.';
  }
  if (raw.includes('OVERTAKE_MISMATCH')) {
    return 'Wyprzedzenie odrzucone. Odświeżyliśmy widok – przelicz wynik i spróbuj ponownie.';
  }
  if (raw.includes('WIN_SCORE_EXCEEDED')) return 'Wynik przekroczyłby 1000 pkt. Zapisz mniejszą kombinację.';
  if (raw.includes('FIRST_ENTRY_TOO_LOW')) return 'Pierwszy dodatni wpis musi mieć min. 50 pkt. Rzuć ponownie i zdobądź 50+.';
  if (raw.includes('GAME_OVER')) return 'Gra została już zakończona. Wyjdź do lobby.';
  if (raw.toLowerCase().includes('load failed')) {
    return 'Nie udało się wysłać ruchu. Sprawdź połączenie i wyślij turę ponownie (np. kliknij “Zapisz 0” albo rzuć jeszcze raz). Gdy błąd wraca, odśwież grę.';
  }
  return 'Nie udało się zapisać tury. Sprawdź połączenie i wyślij turę ponownie (np. “Zapisz 0” lub kolejny rzut).';
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && typeof error.message === 'string') {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error';
};

export function useTurnPersistence({
  gameStateRef,
  dispatch,
  refreshSnapshot,
}: {
  gameStateRef: React.RefObject<GameState>;
  dispatch: React.Dispatch<GameAction>;
  refreshSnapshot?: (reason: string) => Promise<boolean>;
}) {
  const appendWithRetry = React.useCallback(
    async (gameId: string, event: GameEvent, stage: 'TURN_END' | 'SCORE', retries = 5) => {
      const attempt = async () => eventStore.appendEvent(gameId, event);
      let lastError: unknown = null;
      for (let i = 0; i <= retries; i += 1) {
        try {
          const res = await attempt();
          if (res?.success) return res;
          lastError = res?.error || 'appendEvent returned unsuccessful result';
          throw new TurnPersistenceError(stage, typeof lastError === 'string' ? lastError : 'Unknown error');
        } catch (err: unknown) {
          lastError = err;
          const message = getErrorMessage(err).toLowerCase();
          const transient =
            message.includes('failed to fetch') ||
            message.includes('load failed') ||
            message.includes('networkerror') ||
            message.includes('network error');
          const hasNext = i < retries;
          if (!(transient && hasNext)) {
            throw err;
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
      throw new TurnPersistenceError(stage, typeof lastError === 'string' ? lastError : 'Unknown error');
    },
    []
  );

  const recoverFromAppendError = React.useCallback(
    async (errorMessage: string) => {
      const recoverable = [
        'ACTIVE_PLAYER_MISMATCH',
        'ROLL_INDEX_OUT_OF_SYNC',
        'SCORE_MISMATCH',
        'WIN_SCORE_EXCEEDED',
        'OVERTAKE_MISMATCH',
        'OVERTAKE_BONUS_MISMATCH',
        'TUNNEL_EXIT_TOO_LOW',
        'FIRST_ENTRY_TOO_LOW',
        'GAME_OVER',
      ];
      const matched = recoverable.find((c) => errorMessage?.toUpperCase().includes(c));
      if (!matched) return;
      try {
        if (refreshSnapshot) {
          await refreshSnapshot('append-recovery');
          return;
        }
        const gameId = gameStateRef.current?.id;
        if (!gameId) return;
        const snap = await eventStore.getSnapshot(gameId);
        if (snap) dispatch({ type: 'HYDRATE_FROM_SNAPSHOT', snapshot: snap });
      } catch (err: unknown) {
        if (isVerboseLoggingEnabled()) {
          logger.warn('RECOVERY_SNAPSHOT_FAILED', { err: getErrorMessage(err), stage: matched });
        }
      }
    },
    [dispatch, gameStateRef, refreshSnapshot]
  );

  return { appendWithRetry, recoverFromAppendError };
}
